import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '../components/ui';
import { describeLoginError, useLogin, useMe } from '../hooks';
import styles from './LoginPage.module.css';

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();
  const queryClient = useQueryClient();
  const { data: session, isPending } = useMe();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const from = (location.state as LocationState | null)?.from ?? '/tts';

  useEffect(() => {
    queryClient.clear();
  }, [queryClient]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    login.mutate({ username, password }, { onSuccess: () => navigate(from, { replace: true }) });
  };

  if (!isPending && session) {
    return <Navigate to={from} replace />;
  }

  return (
    <div className={styles.shell}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <span className={styles.brandDot} aria-hidden="true" />
          <span className={styles.brandText}>Монтаж TrafficHunters</span>
        </div>

        <h1 className={styles.title}>Вход</h1>

        <form className={styles.form} onSubmit={handleSubmit}>
          <label className={styles.field}>
            <span className={styles.label}>Логин</span>
            <input
              type="text"
              name="username"
              required
              autoFocus
              autoComplete="username"
              className={styles.input}
              placeholder="you@studio.ru"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Пароль</span>
            <input
              type="password"
              name="password"
              required
              autoComplete="current-password"
              className={styles.input}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>

          {login.error && (
            <div className={styles.error} role="alert">
              {describeLoginError(login.error)}
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            disabled={login.isPending}
            className={styles.submit}
          >
            {login.isPending ? 'Входим…' : 'Войти'}
          </Button>
        </form>

        <div className={styles.hint}>Если нет аккаунта, попроси админа создать.</div>
      </div>
    </div>
  );
}
